import type { ReactNode } from 'react';

import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDown,
  CheckCircle2,
  ExternalLink,
  FileText,
  Hash,
  KeyRound,
  Lock,
  Scale,
  Settings,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';

import {
  ROTULO_DO_NIVEL,
  ROTULO_DO_PROVEDOR,
  usePoliticasDeAssinatura,
  useProvedoresAceitos,
} from './dados';

interface EtapaDoFluxo {
  numero: number;
  titulo: string;
  /**
   * Nome curto da etapa, para a faixa de navegação do topo.
   *
   * É separado do `titulo` de propósito: o título é uma frase ("O sistema gera
   * o PDF e calcula o resumo criptográfico") e não cabe numa pílula. O rótulo
   * curto segue a nomenclatura do protótipo, mantendo o vocabulário desta tela
   * — "resumo", e não "hash", como no resto dela.
   */
  rotuloCurto: string;
  descricao: string;
  icone: LucideIcon;
  /** O que, no sistema, controla esta etapa — e onde se configura. */
  configuracao?: { titulo: string; valor: ReactNode; para: string; rotulo: string };
  /** Detalhe técnico, para quem for implementar ou depurar. */
  tecnico?: string;
  /** Defeito de parametrização que impede esta etapa de funcionar hoje. */
  alerta?: string;
}

/**
 * `/admin/parametros/fluxo-assinatura` — o caminho completo de uma assinatura,
 * da abertura do documento ao PDF assinado, e qual parametrização controla cada
 * etapa.
 *
 * É TELA DE LEITURA, e a única das quatro sem botão que precise mentir: não há
 * nada para salvar aqui. Ela existe porque a assinatura digital é parametrizada
 * em três lugares diferentes — operações gov.br, política por documento e
 * provedores aceitos — e ninguém entende o conjunto olhando uma tela de cada
 * vez. Cada etapa aponta para a tela que a controla.
 *
 * Os valores mostrados vêm da parametrização de verdade, não de texto fixo: se a
 * política do termo de adesão ficar sem provedor ativo, a etapa 5 mostra o
 * alerta. O fluxo descrito segue o roteiro de integração da assinatura
 * eletrônica do gov.br.
 */
export function FluxoAssinaturaPage() {
  const politicas = usePoliticasDeAssinatura();
  const provedores = useProvedoresAceitos();

  const carregando = politicas.isLoading || provedores.isLoading;
  const comErro = politicas.isError || provedores.isError;

  const politicaDoTermo = politicas.data?.find((politica) => politica.tipo_documento === 'termo');
  const provedoresDoTermo = (provedores.data ?? [])
    .filter((provedor) => provedor.politica_id === politicaDoTermo?.id && provedor.ativo)
    .sort((a, b) => a.prioridade - b.prioridade);

  const semPolitica = !carregando && !comErro && !politicaDoTermo;
  const semProvedor = !!politicaDoTermo && provedoresDoTermo.length === 0;

  const etapas: EtapaDoFluxo[] = [
    {
      numero: 1,
      titulo: 'O credenciado abre o documento',
      rotuloCurto: 'Acesso',
      descricao: 'No portal logado, ele abre o termo e escolhe assinar.',
      icone: FileText,
    },
    {
      numero: 2,
      titulo: 'Identificação pelo gov.br',
      rotuloCurto: 'Login gov.br',
      descricao:
        'O sistema confere o selo de identidade do cidadão antes de liberar o acesso à assinatura.',
      icone: ShieldCheck,
      configuracao: {
        titulo: 'Selo mínimo exigido',
        valor: <Badge variant="outline">Definido por operação</Badge>,
        para: '/admin/parametros/govbr-operacoes',
        rotulo: 'Operações gov.br',
      },
      tecnico: 'Quem tem selo bronze é recusado quando a operação exige prata ou ouro.',
    },
    {
      numero: 3,
      titulo: 'O sistema gera o PDF e calcula o resumo criptográfico',
      rotuloCurto: 'Resumo do PDF',
      descricao:
        'O documento é montado aqui e o resumo SHA-256 é calculado localmente. O PDF não sai do nosso ambiente.',
      icone: Hash,
      tecnico:
        'Assinatura envelopada usa o resumo do intervalo de bytes; a destacada, o resumo do arquivo inteiro.',
    },
    {
      numero: 4,
      titulo: 'A política do documento é aplicada',
      rotuloCurto: 'Política',
      descricao:
        'O sistema consulta que nível jurídico aquele tipo de documento exige e quais certificados são aceitos.',
      icone: Scale,
      alerta: semPolitica
        ? 'Não há política cadastrada para o termo de adesão — a assinatura não tem regra a seguir.'
        : undefined,
      configuracao: politicaDoTermo
        ? {
            titulo: 'O termo de adesão exige',
            valor: (
              <div className="flex flex-wrap gap-1">
                <Badge>{ROTULO_DO_NIVEL[politicaDoTermo.nivel_minimo]}</Badge>
                {provedoresDoTermo.length > 0 ? (
                  provedoresDoTermo.map((provedor) => (
                    <Badge key={provedor.id} variant="secondary">
                      {ROTULO_DO_PROVEDOR[provedor.provedor]}
                    </Badge>
                  ))
                ) : (
                  <Badge variant="destructive">Sem provedor ativo</Badge>
                )}
              </div>
            ),
            para: '/admin/parametros/politica-assinatura',
            rotulo: 'Política de assinatura',
          }
        : undefined,
    },
    {
      numero: 5,
      titulo: 'O cidadão é levado ao gov.br para autorizar',
      rotuloCurto: 'OAuth',
      descricao:
        'Não é uma tela de leitura do documento: é a autorização para usar o certificado, parecida com um login em outro serviço.',
      icone: KeyRound,
      alerta: semProvedor
        ? 'Nenhum provedor ativo nesta política — a assinatura fica indisponível para o credenciado.'
        : undefined,
      configuracao: {
        titulo: 'Provedores aceitos, na ordem',
        valor: (
          <div className="flex flex-wrap gap-1">
            {provedoresDoTermo.length > 0 ? (
              provedoresDoTermo.map((provedor, indice) => (
                <Badge key={provedor.id} variant="outline">
                  {indice + 1}º {ROTULO_DO_PROVEDOR[provedor.provedor]}
                </Badge>
              ))
            ) : (
              <span className="text-xs text-muted-foreground">Nenhum</span>
            )}
          </div>
        ),
        para: '/admin/parametros/provedores-assinatura',
        rotulo: 'Provedores de assinatura',
      },
      tecnico: 'Autorização OAuth 2.0, com escopo de assinatura e endereço de retorno do sistema.',
    },
    {
      numero: 6,
      titulo: 'O cidadão confirma no gov.br',
      rotuloCurto: 'Autorização',
      descricao:
        'O gov.br pede um código, por mensagem ou pelo aplicativo, para liberar o uso do certificado.',
      icone: Lock,
    },
    {
      numero: 7,
      titulo: 'O sistema assina o resumo',
      rotuloCurto: 'Assinar resumo',
      descricao:
        'Com a autorização em mãos, o servidor envia o resumo à API do gov.br, que devolve o pacote assinado.',
      icone: Settings,
      tecnico: 'O documento em si nunca é enviado — só o resumo criptográfico dele.',
    },
    {
      numero: 8,
      titulo: 'A assinatura é embutida no PDF',
      rotuloCurto: 'Embutir PKCS#7',
      descricao:
        'O pacote é incorporado ao arquivo, ou guardado ao lado dele, e a trilha de assinaturas é gravada.',
      icone: Hash,
      configuracao: {
        titulo: 'Trilha de assinaturas',
        valor: <Badge variant="outline">Somente inserção, encadeada por resumo</Badge>,
        para: '/admin/auditoria',
        rotulo: 'Auditoria',
      },
    },
    {
      numero: 9,
      titulo: 'O documento assinado fica disponível',
      rotuloCurto: 'Concluído',
      descricao:
        'O credenciado recebe o PDF assinado, que qualquer pessoa pode conferir no validador oficial, e as partes são notificadas.',
      icone: CheckCircle2,
    },
  ];

  const alertas = etapas.filter((etapa) => etapa.alerta);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Como funciona a assinatura digital"
        description="O caminho completo de uma assinatura, e qual parametrização do sistema controla cada etapa."
      />

      {/*
        O aviso é FIXO e fica fora do ramo de carregamento de propósito: ele não
        depende de parametrização nenhuma, e é a informação que responde à
        pergunta que todo auditor faz primeiro — para onde vai o documento.
        Escondê-lo enquanto a consulta corre deixaria a dúvida no ar justamente
        no primeiro segundo da tela.
      */}
      <Alert>
        <ShieldCheck className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Importante</AlertTitle>
        <AlertDescription className="text-xs">
          O documento permanece sempre no nosso sistema. O gov.br nunca recebe o PDF — recebe apenas
          o resumo criptográfico SHA-256 e devolve o pacote de assinatura. A interface de assinatura
          é nossa; o gov.br só intermedia a autorização do certificado.
        </AlertDescription>
      </Alert>

      {comErro ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar a parametrização da assinatura agora. Verifique sua conexão e tente novamente."
          primaryAction={{
            label: 'Tentar de novo',
            onClick: () => {
              void politicas.refetch();
              void provedores.refetch();
            },
          }}
        />
      ) : carregando ? (
        <Spinner />
      ) : (
        <>
          {/*
            Faixa de navegação do protótipo: as nove etapas em pílulas, cada uma
            âncora para o cartão correspondente. Serve para dois usos reais —
            saltar direto à etapa que interessa numa tela longa, e ver de
            relance quantas estão com defeito de parametrização, sem rolar.
            A pílula com defeito se distingue por cor E por texto ("precisa de
            atenção" no rótulo acessível), nunca só por cor.
          */}
          <nav aria-label="Etapas da assinatura">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Visão geral em {etapas.length} etapas
              {alertas.length > 0 && (
                <>
                  {' · '}
                  <span className="text-destructive-text">
                    {alertas.length === 1
                      ? '1 precisa de atenção'
                      : `${alertas.length} precisam de atenção`}
                  </span>
                </>
              )}
            </p>
            <ol className="mt-2 flex flex-wrap gap-1.5">
              {etapas.map((etapa) => (
                <li key={etapa.numero}>
                  <a
                    href={`#etapa-${etapa.numero}`}
                    aria-label={
                      etapa.alerta
                        ? `Etapa ${etapa.numero}, ${etapa.rotuloCurto} — precisa de atenção`
                        : `Etapa ${etapa.numero}, ${etapa.rotuloCurto}`
                    }
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                      'transition-colors hover:bg-muted focus-visible:outline-hidden',
                      'focus-visible:ring-2 focus-visible:ring-ring',
                      etapa.alerta
                        ? 'border-destructive/50 bg-destructive/5 text-destructive-text'
                        : 'text-muted-foreground',
                    )}
                  >
                    <span className="font-semibold">{etapa.numero}</span>
                    {etapa.rotuloCurto}
                    {etapa.alerta && <AlertTriangle className="h-3 w-3" aria-hidden="true" />}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {alertas.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>
                {alertas.length === 1
                  ? '1 etapa não funcionaria com a parametrização atual'
                  : `${alertas.length} etapas não funcionariam com a parametrização atual`}
              </AlertTitle>
              <AlertDescription className="space-y-1 text-xs">
                {alertas.map((etapa) => (
                  <div key={etapa.numero}>
                    <strong>Etapa {etapa.numero}:</strong> {etapa.alerta}
                  </div>
                ))}
              </AlertDescription>
            </Alert>
          )}

          <ol className="space-y-0">
            {etapas.map((etapa, indice) => (
              <li key={etapa.numero}>
                <CartaoDaEtapa etapa={etapa} ultima={indice === etapas.length - 1} />
              </li>
            ))}
          </ol>

          <Alert>
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>Conferência pública</AlertTitle>
            <AlertDescription className="text-xs">
              Qualquer pessoa pode conferir a validade de um documento assinado no validador oficial
              do Instituto Nacional de Tecnologia da Informação, sem precisar de conta no sistema.
            </AlertDescription>
          </Alert>
        </>
      )}
    </div>
  );
}

interface CartaoDaEtapaProps {
  etapa: EtapaDoFluxo;
  ultima: boolean;
}

function CartaoDaEtapa({ etapa, ultima }: CartaoDaEtapaProps) {
  const Icone = etapa.icone;

  return (
    // `scroll-mt`: a casca tem cabeçalho e faixa de breadcrumb fixos, e sem a
    // margem de rolagem a âncora pararia com o cartão escondido atrás deles.
    <div id={`etapa-${etapa.numero}`} className="relative scroll-mt-28">
      <Card className={cn(etapa.alerta && 'border-destructive/50')}>
        <CardContent className="flex gap-4 p-4">
          <div className="flex flex-col items-center gap-2">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <Icone className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            <span className="text-xs font-semibold text-muted-foreground">{etapa.numero}</span>
          </div>

          <div className="min-w-0 flex-1 space-y-2">
            <div>
              <h3 className="text-sm font-semibold">{etapa.titulo}</h3>
              <p className="mt-0.5 text-sm text-muted-foreground">{etapa.descricao}</p>
            </div>

            {etapa.alerta && (
              <p className="rounded-lg bg-destructive/5 px-3 py-2 text-xs text-destructive-text">
                {etapa.alerta}
              </p>
            )}

            {etapa.configuracao && (
              <div className="rounded-lg border bg-muted/30 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    {etapa.configuracao.titulo}
                  </span>
                  <Button asChild variant="ghost" size="sm" className="h-7">
                    <Link to={etapa.configuracao.para}>{etapa.configuracao.rotulo}</Link>
                  </Button>
                </div>
                <div className="mt-2">{etapa.configuracao.valor}</div>
              </div>
            )}

            {etapa.tecnico && <p className="text-xs text-muted-foreground">{etapa.tecnico}</p>}
          </div>
        </CardContent>
      </Card>

      {!ultima && (
        <div className="flex justify-center py-1">
          <ArrowDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

export default FluxoAssinaturaPage;
